// src/pages/Reports/ReportExamRegistrations.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { Check, Download } from "lucide-react";

export default function ReportExamRegistrations() {
  const [sessions, setSessions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedSubjectCode, setSelectedSubjectCode] = useState("All");

  // Parent registrations (joined student + session) for the selected session
  const [parents, setParents] = useState([]); // [{ id, student_id, student_name, contact_no, email, venue, total_fee_paid, payment_date, remarks }]
  // Child rows for those parents
  const [children, setChildren] = useState([]); // [{ session_registration_id, subject_id }]

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // ---------- Load dropdowns ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: sess, error: sErr }, { data: subs, error: subErr }] = await Promise.all([
          supabase
            .from("exam_sessions")
            .select("id, name, start_date")
            .order("start_date", { ascending: false })
            .order("name", { ascending: true }),
          supabase.from("subjects").select("id, code, name").order("code", { ascending: true }),
        ]);
        if (sErr) throw sErr;
        if (subErr) throw subErr;

        if (!cancelled) {
          setSessions(sess || []);
          setSubjects(subs || []);
          // auto-select latest session if none chosen
          if (!selectedSessionId && (sess || []).length > 0) {
            setSelectedSessionId((sess || [])[0].id);
          }
        }
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load filters.");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Load data when session changes ----------
  const loadData = useCallback(async () => {
    if (!selectedSessionId) return;
    setLoading(true);
    setErr("");
    try {
      // Parents for this session
      const { data: parentRows, error: pErr } = await supabase
        .from("student_exam_session")
        .select(
          `
          id, student_id, exam_session_id, venue, total_fee_paid, payment_date, remarks, is_deleted,
          students:students ( id, full_name, email, contact_no ),
          exam_sessions:exam_sessions ( id, name )
        `
        )
        .eq("exam_session_id", selectedSessionId)
        .eq("is_deleted", false);

      if (pErr) throw pErr;

      const normalizedParents =
        (parentRows || []).map((r) => ({
          id: r.id,
          student_id: r.student_id,
          student_name: r.students?.full_name || "—",
          contact_no: r.students?.contact_no || "—",
          email: r.students?.email || "—",
          venue: r.venue || "",
          total_fee_paid:
            r.total_fee_paid == null || r.total_fee_paid === ""
              ? null
              : Number(r.total_fee_paid),
          payment_date: r.payment_date || null,
          remarks: r.remarks || "",
        })) || [];

      const parentIds = normalizedParents.map((p) => p.id);
      let childRows = [];
      if (parentIds.length > 0) {
        const { data: kids, error: cErr } = await supabase
          .from("student_exam_info")
          .select("session_registration_id, subject_id, is_deleted")
          .eq("is_deleted", false)
          .in("session_registration_id", parentIds);
        if (cErr) throw cErr;
        childRows = kids || [];
      }

      setParents(normalizedParents);
      setChildren(childRows);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Failed to load exam registrations.");
    } finally {
      setLoading(false);
    }
  }, [selectedSessionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ---------- Subject options ----------
  const subjectOptions = useMemo(
    () => ["All", ...subjects.map((s) => s.code)],
    [subjects]
  );

  const subjectCodeToId = useMemo(() => {
    const map = new Map();
    subjects.forEach((s) => map.set(s.code, s.id));
    return map;
  }, [subjects]);

  const selectedSubjectId = useMemo(() => {
    if (selectedSubjectCode === "All") return null;
    return subjectCodeToId.get(selectedSubjectCode) || null;
  }, [selectedSubjectCode, subjectCodeToId]);

  // Use all subject codes in the system as the matrix columns (sorted)
  const matrixSubjectCodes = useMemo(
    () =>
      subjects
        .map((s) => s.code)
        .filter(Boolean)
        .sort((a, b) => {
          // numeric-aware sort, e.g. PS5001 < PS50010
          const collator = new Intl.Collator("en", {
            sensitivity: "base",
            numeric: true,
          });
          return collator.compare(a, b);
        }),
    [subjects]
  );

  const subjectIdByCode = useMemo(() => {
    const m = {};
    subjects.forEach((s) => (m[s.code] = s.id));
    return m;
  }, [subjects]);

  // ---------- Build UI rows ----------
  // Matrix (Subject = All)
  const matrixRows = useMemo(() => {
    if (!selectedSessionId || selectedSubjectCode !== "All") return [];

    // Build lookup of child: registration_id -> Set(subject_id)
    const subjSetByParent = new Map();
    for (const c of children) {
      if (!subjSetByParent.has(c.session_registration_id)) {
        subjSetByParent.set(c.session_registration_id, new Set());
      }
      subjSetByParent.get(c.session_registration_id).add(c.subject_id);
    }

    // Build rows per parent
    return parents
      .slice()
      .sort((a, b) => a.student_name.localeCompare(b.student_name, undefined, { numeric: true }))
      .map((p) => {
        const set = subjSetByParent.get(p.id) || new Set();
        const flags = {};
        for (const code of matrixSubjectCodes) {
          const sid = subjectIdByCode[code];
          flags[code] = sid ? set.has(sid) : false;
        }
        const countInSession = set.size;
        return {
          id: p.student_id,
          full_name: p.student_name,
          contact_no: p.contact_no,
          email: p.email,
          subjectFlags: flags,
          countInSession,
          venue: p.venue || "",
          total_fee_paid: p.total_fee_paid,
          payment_date: p.payment_date,
          remarks: p.remarks || "",
        };
      });
  }, [
    selectedSessionId,
    selectedSubjectCode,
    parents,
    children,
    matrixSubjectCodes,
    subjectIdByCode,
  ]);

  // Single subject list
  const singleRows = useMemo(() => {
    if (!selectedSessionId || !selectedSubjectId || selectedSubjectCode === "All") return [];
    // Find parents that have a child with the selected subject
    const parentIdsWithSubject = new Set(
      children
        .filter((c) => c.subject_id === selectedSubjectId)
        .map((c) => c.session_registration_id)
    );
    const filteredParents = parents.filter((p) => parentIdsWithSubject.has(p.id));
    return filteredParents
      .slice()
      .sort((a, b) => a.student_name.localeCompare(b.student_name, undefined, { numeric: true }))
      .map((p) => ({
        id: p.student_id,
        full_name: p.student_name,
        contact_no: p.contact_no,
        email: p.email,
      }));
  }, [selectedSessionId, selectedSubjectId, selectedSubjectCode, parents, children]);

  // ---------- Helpers for display/export ----------
  const sessionName = useMemo(
    () => sessions.find((s) => s.id === selectedSessionId)?.name || "",
    [sessions, selectedSessionId]
  );

  const csvEscape = (val) => {
    const s = val == null ? "" : String(val);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const fmtMYR = (n) =>
    n == null
      ? ""
      : `RM${Number(n).toLocaleString("en-MY", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

  const fmtDate = (d) => {
    if (!d) return "";
    try {
      return new Date(d).toLocaleDateString("en-GB", {
        timeZone: "Asia/Kuala_Lumpur",
      });
    } catch {
      return d;
    }
  };

  const todayKL = useMemo(() => {
    const y = new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    const m = new Intl.DateTimeFormat("en-GB", {
      month: "2-digit",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    const d = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      timeZone: "Asia/Kuala_Lumpur",
    }).format(new Date());
    return `${y}${m}${d}`; // YYYYMMDD
  }, []);

  const normalizedSession = (sessionName || "").replaceAll("/", "_");

  const buildMatrixCsv = () => {
    const headers = [
      "No.",
      "Student Name",
      "Contact Number",
      "Email Address",
      ...matrixSubjectCodes,
      "Exam Venue",
      "Total Exam Fee Paid",
      "Date of Payment",
      "Remarks",
    ];
    const lines = [headers.map(csvEscape).join(",")];

    matrixRows.forEach((row, idx) => {
      const cols = [
        idx + 1,
        row.full_name,
        row.contact_no,
        row.email,
        ...matrixSubjectCodes.map((code) => (row.subjectFlags[code] ? "Y" : "")),
        row.venue || "",
        fmtMYR(row.total_fee_paid),
        fmtDate(row.payment_date),
        row.remarks || "",
      ];
      lines.push(cols.map(csvEscape).join(","));
    });

    const filename = `exam_registrations_${normalizedSession}_AllSubjects_${todayKL}.csv`;
    return { csv: lines.join("\n"), filename };
  };

  const buildSingleCsv = () => {
    const headers = ["No.", "Student Name", "Contact Number", "Email Address"];
    const lines = [headers.map(csvEscape).join(",")];
    singleRows.forEach((row, idx) => {
      const cols = [idx + 1, row.full_name, row.contact_no, row.email];
      lines.push(cols.map(csvEscape).join(","));
    });
    const filename = `exam_registrations_${normalizedSession}_${selectedSubjectCode}_${todayKL}.csv`;
    return { csv: lines.join("\n"), filename };
  };

  const hasMatrix = selectedSessionId && selectedSubjectCode === "All" && matrixRows.length > 0;
  const hasSingle =
    selectedSessionId && selectedSubjectCode !== "All" && singleRows.length > 0;
  const exportEnabled = Boolean(hasMatrix || hasSingle);

  const handleExportCsv = () => {
    const { csv, filename } =
      selectedSubjectCode === "All" ? buildMatrixCsv() : buildSingleCsv();

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Small chip for header summary
  const Chip = ({ children, tone = "indigo" }) => (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs
        ${tone === "indigo" ? "border-indigo-200 bg-indigo-50 text-indigo-700" : ""}
        ${tone === "sky" ? "border-sky-200 bg-sky-50 text-sky-700" : ""}
        ${tone === "slate" ? "border-slate-200 bg-slate-50 text-slate-700" : ""}`}
    >
      {children}
    </span>
  );

  return (
    <div className="p-2 md:p-2">
      {/* Header card with gradient accent */}
      <div className="mb-4">
        <div className="relative overflow-hidden rounded-xl border border-slate-200 shadow-sm">
          <div className="h-2 w-full bg-gradient-to-r from-indigo-500 via-sky-400 to-cyan-400" />
          <div className="bg-white p-4 md:p-5">
            <h1 className="text-xl font-semibold text-slate-800">
              Exam Session Registration Report
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Filter by exam session and subject. Click a student to view details.
            </p>

            {/* Live filter chips */}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Chip tone="indigo">Session: {sessionName || "-"}</Chip>
              <Chip tone="sky">
                Subject:{" "}
                {selectedSubjectCode === "All"
                  ? "All"
                  : subjects.find((s) => s.code === selectedSubjectCode)?.name ||
                    selectedSubjectCode}
              </Chip>
            </div>

            {/* Filters + Export */}
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[14rem,18rem,1fr] md:items-end">
              {/* Exam Session */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Exam Session
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                  value={selectedSessionId}
                  onChange={(e) => setSelectedSessionId(e.target.value)}
                  disabled={loading || !!err}
                >
                  {!sessions.length && <option>No sessions found</option>}
                  {sessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Subject
                </label>
                <select
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-sky-300"
                  value={selectedSubjectCode}
                  onChange={(e) => setSelectedSubjectCode(e.target.value)}
                >
                  {subjectOptions.map((code) => (
                    <option key={code} value={code}>
                      {code === "All"
                        ? "All"
                        : subjects.find((s) => s.code === code)?.name || code}
                    </option>
                  ))}
                </select>
              </div>

              {/* Export */}
              <div className="flex justify-start md:justify-end">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  disabled={!exportEnabled}
                  title="Export what’s on screen"
                  className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium shadow-sm
                    ${
                      exportEnabled
                        ? "bg-sky-600 text-white hover:bg-sky-700"
                        : "bg-slate-100 text-slate-400 cursor-not-allowed"
                    }`}
                >
                  <Download className="h-4 w-4" strokeWidth={2.5} />
                  Export CSV
                </button>
              </div>
            </div>

            {err ? (
              <p className="mt-2 text-xs text-red-600">
                {err}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Content Card */}
      <div className="mt-4 bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="p-3 md:p-4">
          {/* Subject = All → Matrix */}
          {selectedSessionId && selectedSubjectCode === "All" && (
            <>
              {loading ? (
                <div className="text-xs text-slate-500">Loading…</div>
              ) : matrixRows.length === 0 ? (
                <div className="text-xs text-slate-500">
                  No registrations for this session.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-[1400px] w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="text-left text-slate-600 border-b">
                        <th className="py-2 pr-2">No.</th>
                        <th className="py-2 pr-2">Student Name</th>
                        <th className="py-2 pr-2">Contact Number</th>
                        <th className="py-2 pr-2">Email Address</th>
                        {matrixSubjectCodes.map((code) => (
                          <th key={code} className="py-1 px-1.5 text-center">
                            <span className="rounded bg-sky-50 px-1.5 py-0.5 text-sky-700 border border-sky-100 text-[11px]">
                              {code}
                            </span>
                          </th>
                        ))}
                        <th className="py-2 pr-2">Exam Venue</th>
                        <th className="py-2 pr-2">Total Exam Fee Paid</th>
                        <th className="py-2 pr-2">Date of Payment</th>
                        <th className="py-2 pr-2">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matrixRows.map((row, idx) => (
                        <tr
                          key={`${row.id}-${idx}`}
                          className={`border-b last:border-0 ${
                            idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                          } hover:bg-sky-50/60`}
                        >
                          <td className="py-2 pr-2">{idx + 1}</td>
                          <td className="py-2 pr-2">
                            <Link
                              to={{
                                pathname: `/students/${row.id}`,
                                search: "?from=exam-report",
                              }}
                              state={{ from: "exam-report" }}
                              className="font-medium text-slate-800 hover:text-sky-700 hover:underline"
                            >
                              {row.full_name}
                            </Link>
                          </td>
                          <td className="py-2 pr-2">{row.contact_no}</td>
                          <td className="py-2 pr-2">{row.email}</td>

                          {matrixSubjectCodes.map((code) => (
                            <td
                              key={code}
                              className="py-2 px-1.5 text-center"
                              title={row.subjectFlags[code] ? "Registered" : "Not registered"}
                            >
                              {row.subjectFlags[code] ? (
                                <span className="inline-flex items-center justify-center rounded bg-emerald-50 p-0.5 text-emerald-700 border border-emerald-100">
                                  <Check size={12} strokeWidth={2.5} />
                                </span>
                              ) : (
                                ""
                              )}
                            </td>
                          ))}

                          <td className="py-2 pr-2">{row.venue || "—"}</td>
                          <td className="py-2 pr-2">
                            {row.total_fee_paid == null
                              ? "—"
                              : `RM${Number(row.total_fee_paid).toLocaleString("en-MY", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}
                          </td>
                            <td className="py-2 pr-2">
                            {row.payment_date
                                ? new Date(row.payment_date).toLocaleDateString("en-GB", {
                                    timeZone: "Asia/Kuala_Lumpur",
                                })
                                : "—"}
                            </td>
                          <td className="py-2 pr-2">{row.remarks || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-2 text-[11px] text-slate-500">
                    Showing {matrixRows.length} students • Session: {sessionName || "-"} •
                    Subject: All
                  </div>
                </div>
              )}
            </>
          )}

          {/* Subject = specific code → Simple List */}
          {selectedSessionId && selectedSubjectCode !== "All" && (
            <>
              {loading ? (
                <div className="text-xs text-slate-500">Loading…</div>
              ) : singleRows.length === 0 ? (
                <div className="text-xs text-slate-500">
                  No students found for this subject in the selected session.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-[600px] w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="text-left text-slate-600 border-b">
                        <th className="py-2 pr-2">No.</th>
                        <th className="py-2 pr-2">Student Name</th>
                        <th className="py-2 pr-2">Contact Number</th>
                        <th className="py-2 pr-2">Email Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {singleRows.map((row, idx) => (
                        <tr
                          key={`${row.id}-${idx}`}
                          className={`border-b last:border-0 ${
                            idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                          } hover:bg-sky-50/60`}
                        >
                          <td className="py-2 pr-2">{idx + 1}</td>
                          <td className="py-2 pr-2">
                            <Link
                              to={{
                                pathname: `/students/${row.id}`,
                                search: "?from=exam-report",
                              }}
                              state={{ from: "exam-report" }}
                              className="font-medium text-slate-800 hover:text-sky-700 hover:underline"
                            >
                              {row.full_name}
                            </Link>
                          </td>
                          <td className="py-2 pr-2">{row.contact_no}</td>
                          <td className="py-2 pr-2">{row.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-2 text-[11px] text-slate-500">
                    Showing {singleRows.length} students • Session: {sessionName || "-"} •
                    Subject:{" "}
                    {subjects.find((s) => s.code === selectedSubjectCode)?.name ||
                      selectedSubjectCode}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Placeholder */}
          {!selectedSessionId && (
            <div className="text-xs text-slate-500">
              Select an exam session to view registrations.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
